/**
 * 分类下拉的**候选口径** —— 「空输入列什么、键入搜什么」（`DR-066` §5 · `AC-B5.8` / `AC-B5.9`）。
 *
 * ## 🔴 为什么从「常用集」改成「两段式下钻」
 *
 * 批 `110` 用 `idleSubset` 把空输入收窄成"常用 12 个一级"，副作用是
 * **二级必须键入才出现**（负责人 2026-10-28 第 1 条："二级要打字才出来，不好用"）。
 * 而完全摊平（全部 L1 + 全部 L2 一次列出）又太长（支出 18 + 107），
 * 拆分行里每一行都撑成一个长列表。
 *
 * ⇒ **两段式**：
 *
 * | 状态 | 下拉里列什么 |
 * |---|---|
 * | 没打字 | **全部 L1**；已展开的 L1 **紧跟其后插入它的全部 L2** |
 * | 一旦键入 | **全量搜**（跨级，未展开的 L2 也搜得到） |
 *
 * ⇒ 首屏只有一级（可扫读）、点 `▸` 就地展开（不用离开当前框）、键入仍是全量（不制造"搜不到"）。
 *
 * ## ⚠️ 三条边界
 *
 * 1. **顺序不在本文件重排** —— 一律用 `all` 的传入顺序（= 服务端行序，`toOptions` 已按树序摊平）。
 * 2. **L2 的归属靠 `parentId` 显式判断**，不靠"在数组里挨着谁" ——
 *    后者会让"扁平序输入"（先全部 L1 再全部 L2）静默丢光所有二级。
 * 3. 🔴 **断言只能打这个纯函数**：MUI 弹层在 `renderToStaticMarkup` 下**完全不挂载**，
 *    绕道 DOM 抓 `MuiAutocomplete-option` 只能拿到空列表（假信号，批 38 / 110 各踩一次）。
 */

import { searchByName } from '../lib/pinyin-match.js';
import type { SearchSelectOption } from '../ui/SearchSelect.js';

/** 本函数需要的最小选项形状（由 `CategoryPicker.toOptions()` 产出） */
export interface CategoryTreeOption extends SearchSelectOption {
  /** 层级：1 = 大类，2 = 二级细分 */
  readonly level: 1 | 2;
  /** 父级 id（L1 为 `null`）—— 🔴 展开态靠它判断归属，不靠数组相邻 */
  readonly parentId: string | null;
  /** 该 L1 有几个二级（L2 恒 0）—— 决定行尾是否渲染 `▸` */
  readonly childCount: number;
}

/**
 * 计算下拉候选。
 *
 * @param all 全量选项（**树序**：每个 L1 紧跟它的 L2；本函数不重排）
 * @param expanded 已展开的 L1 id 集合
 * @param query 输入框当前文本（空白串 = 没打字）
 */
export function visibleCategoryOptions<T extends CategoryTreeOption>(
  all: readonly T[],
  expanded: ReadonlySet<string>,
  query: string,
): T[] {
  // ① 键入 ⇒ 全量搜，**忽略展开态**（否则"没展开的二级搜不到"会变成新的静默降级）
  if (query.trim() !== '') {
    return searchByName(all, (o) => o.matchText ?? o.label, query);
  }

  // ② 空输入 ⇒ 全部 L1；已展开的 L1 紧跟其后插入它的全部 L2（顺序 = `all` 里的相对顺序）
  const out: T[] = [];
  for (const o of all) {
    if (o.level !== 1) continue;
    out.push(o);
    if (!expanded.has(o.id)) continue;
    for (const child of all) {
      if (child.level === 2 && child.parentId === o.id) out.push(child);
    }
  }
  return out;
}

/** 行尾是否该有展开按钮（有二级、且是 L1）—— 与 `visibleCategoryOptions` 同一份数据 */
export function isExpandable(o: CategoryTreeOption): boolean {
  return o.level === 1 && o.childCount > 0;
}

/** 展开态切换（返回新集合 —— 不就地改 `Set`，否则 React 看不见变化） */
export function toggleExpanded(expanded: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(expanded);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}
